// MissionImpactAnalytics — the full Mission Impact & Resource Alignment body
// (FS-CMD-2), extracted so it can render in two places during the merge window:
//   1. as the Analytics view of the Executive Command Center (/executive?view=analytics)
//   2. via the legacy /analytics/mission-impact route wrapper, which stays alive
//      until the nav-consolidation ticket ships the redirect (02 §A).
//
// Trust contract carried over and hardened here:
//   - the KPI hero band shows a real `asOf` from the actual data load
//   - no fabricated fallbacks: no "+12% vs FY25" delta, no invented ROI
//     (the old missionValueScore/40 default-70 derivation is removed) — when
//     the payload lacks a metric the gauge shows an honest absent state
//   - the mission-outcome gauge may be DERIVED from real portfolio
//     mission-impact scores only, and is labeled as derived
//   - the reallocation advisor renders exclusively from governed server-side
//     AI runs; Accept creates a real draft decision-support record.
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApi, useMutation } from '../../lib/useApi.js';
import { api } from '../../lib/apiClient.js';
import { useClientPagination } from '../../lib/usePagination.js';
import {
  PageActionStrip,
  ErrorState,
  EmptyState,
  PredictiveSlippage,
  GaugeCard,
  MissionImpactHeatmap,
  AiRecommendationCard,
  BriefingExportCard,
  PageViewerControls,
  KpiHeroBand,
  KpiBandSkeleton,
  ChartSkeleton,
  toast,
  activityRail,
} from '../../components/index.js';
import { humanizeEnum } from '../../lib/fieldLabels.js';
import './missionImpact.css';
import './executive.redesign.css';

// Render a run timestamp as a readable local date-time instead of a raw ISO
// string; falls back to the raw value if it isn't a parseable date.
function formatRunTimestamp(value) {
  if (!value) return 'Pending';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

export function MissionImpactAnalytics() {
  const navigate = useNavigate();
  const summary = useApi('/analytics/mission-impact');
  const portfolios = useApi('/portfolios', { query: { limit: 50 } });
  const kpis = useApi('/mission-kpis', { query: { limit: 50 } });
  const briefingsRes = useApi('/briefings', { query: { limit: 40 } });
  // Governed AI runs only — the reallocation advisor renders exclusively from
  // a server-side run record (seeded or generated), never client fabrication.
  // Wide page (runs are ordered started_at DESC and the kind filter is applied
  // server-side per page) so newer ad-hoc runs cannot page the advisor run out.
  const advisorRuns = useApi('/ai/runs', {
    query: { kind: 'reallocation_advisor', limit: 200 },
  });
  const briefingMut = useMutation();
  const approveMut = useMutation();
  const exportMut = useMutation();
  const acceptMut = useMutation();
  const [dismissedRecIds, setDismissedRecIds] = useState([]);

  const portfolioList = useMemo(() => portfolios.data?.data || [], [portfolios.data?.data]);
  const kpiList = kpis.data?.data || [];
  // Surface briefings tied to a portfolio / project / decision scope so the
  // mission-impact rail looks lived-in.  Cap at 6 so the grid stays scannable.
  const briefings = (briefingsRes.data?.data || []).filter((b) => {
    const t = b.title || '';
    const s = String(b.scopeType || '').toLowerCase();
    return (
      /portfolio|mission|executive|decision|risk|brief/i.test(t) ||
      ['portfolio', 'project', 'decision'].includes(s)
    );
  });
  const kpiPager = useClientPagination(kpiList, {
    initialPageSize: 8,
    resetKey: `mission-kpis-${kpiList.length}`,
  });
  const briefingPager = useClientPagination(briefings, {
    initialPageSize: 5,
    resetKey: `mission-briefings-${briefings.length}`,
  });

  const summaryData = summary.data?.data || {};
  const heatmap = summaryData.heatmap || { rows: [], columns: [], cells: [] };
  const missionGauges = summaryData.gauges || {};
  const predictivePoints = summaryData.predictiveSlippage?.points || [];

  const totalPortfolios = portfolioList.length;
  // Mission mean derives ONLY from portfolios that actually report a
  // mission-impact score — no zero-fill, no default. When nothing real exists
  // the gauge yields to an honest absent state.
  const missionScores = portfolioList
    .map((p) => p.health?.missionImpactScore)
    .filter((score) => typeof score === 'number' && !Number.isNaN(score));
  const avgMission = missionScores.length
    ? Math.round(missionScores.reduce((sum, score) => sum + score, 0) / missionScores.length)
    : null;
  const roiValue = missionGauges.aggregateRoi?.value;
  const missionOutcomeValue = missionGauges.missionOutcome?.value ?? avgMission;
  const derivedMissionLabel = `Derived · mean of ${missionScores.length} portfolio mission-impact score${
    missionScores.length === 1 ? '' : 's'
  }`;

  // KPI hero band — built only from data already fetched. ROI renders only
  // when the mission-impact payload reports it (no client-side ROI formula);
  // the outcome tile may fall back to the derived portfolio mean, labeled.
  const atTargetKpis = kpiList.filter((k) => {
    const cur = Number(k.currentValue ?? k.value);
    const target = Number(k.target ?? k.targetValue);
    return Number.isFinite(cur) && Number.isFinite(target) && cur >= target;
  }).length;
  const heroItems = [
    {
      id: 'mi-aggregate-roi',
      label: missionGauges.aggregateRoi?.label || 'Aggregate ROI',
      value: roiValue != null ? Number(roiValue) : '—',
      unit: roiValue != null ? missionGauges.aggregateRoi?.unit || 'x' : undefined,
      status: roiValue != null ? missionGauges.aggregateRoi?.tone || 'sage' : 'slate',
      statusLabel: roiValue != null ? missionGauges.aggregateRoi?.deltaLabel : undefined,
      footer:
        roiValue != null
          ? 'Mission value relative to planned spend'
          : 'Not reported by the mission-impact source',
    },
    {
      id: 'mi-mission-outcome',
      label: missionGauges.missionOutcome?.label || 'Mission outcome',
      value: missionOutcomeValue != null ? Number(missionOutcomeValue) : '—',
      status: missionOutcomeValue != null ? missionGauges.missionOutcome?.tone || 'blue' : 'slate',
      statusLabel:
        missionGauges.missionOutcome?.value != null
          ? missionGauges.missionOutcome?.statusLabel || missionGauges.missionOutcome?.sublabel
          : undefined,
      footer:
        missionGauges.missionOutcome?.value != null
          ? `${totalPortfolios} portfolios scored`
          : missionOutcomeValue != null
            ? derivedMissionLabel
            : 'No portfolio reports a mission-impact score',
    },
    {
      id: 'mi-kpis-tracked',
      label: 'KPIs tracked',
      value: kpiList.length,
      status: 'slate',
      footer: `${atTargetKpis} at or above target`,
    },
    {
      id: 'mi-portfolios',
      label: 'Portfolios',
      value: totalPortfolios,
      status: 'blue',
      footer: `${missionScores.length} of ${totalPortfolios} report a mission score`,
    },
  ];

  // Latest completed governed advisor run. Recommendations exist only inside
  // its server-persisted content payload; there is no client-side fallback.
  const advisorRun = useMemo(() => {
    const runs = (advisorRuns.data?.data || []).filter(
      (r) =>
        r.status === 'completed' &&
        Array.isArray(r.content?.recommendations) &&
        r.content.recommendations.length > 0,
    );
    return (
      runs.sort(
        (a, b) =>
          new Date(b.completedAt || b.createdAt || 0) - new Date(a.completedAt || a.createdAt || 0),
      )[0] || null
    );
  }, [advisorRuns.data]);

  const advisorRecommendations = useMemo(() => {
    if (!advisorRun) return [];
    return advisorRun.content.recommendations
      .filter((rec) => !dismissedRecIds.includes(rec.id))
      .map((rec) => ({ ...rec, actionLabel: 'Accept' }));
  }, [advisorRun, dismissedRecIds]);

  const advisorReviewLabel = advisorRun
    ? String(advisorRun.reviewState || advisorRun.humanReviewState || 'unreviewed')
        .replace(/_/g, ' ')
        .replace(/^./, (c) => c.toUpperCase())
    : null;

  // Accept = governed handoff: persist a DRAFT decision-support record (a
  // human still decides — the AI path never changes record state), then move
  // the user to the decision workspace where that draft is worked.
  async function acceptRecommendation(rec) {
    if (!advisorRun) return;
    try {
      const created = await acceptMut.run(() =>
        api.post('/decisions', {
          scopeType: rec.targetPortfolioId ? 'portfolio' : advisorRun.scopeType || 'portfolio',
          scopeId: rec.targetPortfolioId || advisorRun.scopeId,
          decisionType: 'reallocation',
          status: 'draft',
          decision: rec.title,
          rationale: `${rec.body} Source: governed AI run ${advisorRun.id} (${advisorRun.modelId || 'model unrecorded'}), pending human review.`,
          sourceAiRunId: advisorRun.id,
          sourceRecommendationId: rec.id,
          relatedArtifactIds: (rec.citations || []).map((c) => c.source).filter(Boolean),
        }),
      );
      const newId = created.data?.id;
      toast.success({
        title: 'Draft decision created',
        body: `${newId} routed to the decision workspace for human review.`,
      });
      activityRail.push({
        action: 'decision.draft.create',
        targetType: 'decision',
        targetId: newId,
        actorUserId: 'you',
        summary: rec.title,
      });
      navigate('/decision-support');
    } catch (e) {
      toast.error({ title: 'Could not create the draft decision', body: e.message });
    }
  }

  function dismissRecommendation(rec) {
    setDismissedRecIds((ids) => [...ids, rec.id]);
    toast.show({ title: 'Dismissed from view', body: rec.title });
  }

  async function generateBriefing() {
    try {
      const draft = await briefingMut.run(() =>
        api.post('/ai/generate/briefing', {
          prompt:
            'Draft a quarterly mission-impact executive paper covering portfolio outcomes and reallocation recommendations.',
        }),
      );
      const aiRunId = draft.data?.id;
      const created = await api.post('/briefings', {
        title: 'Mission Impact — Quarterly Executive Brief',
        scopeType: 'portfolio',
        scopeId: 'PF-PORTFOLIO-EXEC',
        format: 'html',
        aiRunId,
      });
      toast.success({
        title: 'Briefing draft created',
        body: `${created.data?.id} pending review · ${draft.data?.citations?.length || 0} citations attached.`,
      });
      activityRail.push({
        action: 'briefing.create',
        targetType: 'briefing',
        targetId: created.data?.id,
        actorUserId: 'you',
        summary: 'Quarterly mission-impact briefing draft',
      });
      briefingsRes.data &&
        (briefingsRes.data.data = [created.data, ...(briefingsRes.data.data || [])]);
    } catch (e) {
      toast.error({ title: 'Could not generate briefing', body: e.message });
    }
  }

  async function exportBriefing(b, format) {
    try {
      const res = await exportMut.run(() => api.post(`/briefings/${b.id}/export`, { format }));
      toast.success({
        title: `${format.toUpperCase()} export ready`,
        body: `Saved to ${res.data?.outputPath}`,
      });
      activityRail.push({
        action: 'briefing.export',
        targetType: 'briefing',
        targetId: b.id,
        actorUserId: 'you',
        summary: `${format} export`,
      });
    } catch (e) {
      toast.error({ title: 'Export failed', body: e.message });
    }
  }

  async function approveBriefing(b) {
    try {
      await approveMut.run(() => api.post(`/briefings/${b.id}/approve`, {}));
      toast.success({ title: 'Briefing approved', body: `${b.id} signed off and audited.` });
      activityRail.push({
        action: 'briefing.approve',
        targetType: 'briefing',
        targetId: b.id,
        actorUserId: 'you',
      });
      briefingsRes.data &&
        (briefingsRes.data.data = briefingsRes.data.data.map((x) =>
          x.id === b.id ? { ...x, status: 'approved' } : x,
        ));
    } catch (e) {
      toast.error({ title: 'Approve failed', body: e.message });
    }
  }

  if (summary.loading)
    return (
      <div className="mix-loading">
        <KpiBandSkeleton count={4} />
        <div className="mix-loading-charts">
          <ChartSkeleton height={420} />
          <ChartSkeleton height={420} />
        </div>
        <ChartSkeleton height={280} />
      </div>
    );
  if (summary.error)
    return (
      <div data-testid="heatmap-error-state">
        <ErrorState error={summary.error} onRetry={summary.refresh} />
      </div>
    );

  return (
    <>
      <PageActionStrip
        actions={
          <button
            className="btn primary tactile-press"
            onClick={generateBriefing}
            disabled={briefingMut.running}
          >
            {briefingMut.running ? 'Drafting…' : 'Generate executive paper'}
          </button>
        }
      />

      <KpiHeroBand
        className="mix-hero"
        eyebrow="Mission impact rollup"
        asOf={summary.loadedAt}
        items={heroItems}
        columns={4}
      />

      <section className="bento mi-bento">
        <div className="span-8 card mi-slippage">
          <header className="row spread">
            <div>
              <div className="eyebrow">Predictive slippage</div>
              <h2 className="card-title">Forecasted timeline drift · FY26</h2>
            </div>
            <span className="chip blue sm">8-month outlook</span>
          </header>
          <PredictiveSlippage
            data={predictivePoints}
            baselineLabel="Planned"
            predictedLabel="AI-projected"
            unit=" wks"
            height={420}
            surface="mission-impact"
          />
        </div>
        <div className="span-4 stack">
          {missionGauges.aggregateRoi?.value != null ? (
            <GaugeCard
              id={missionGauges.aggregateRoi.id || 'aggregate-roi'}
              value={missionGauges.aggregateRoi.value}
              max={4}
              unit={missionGauges.aggregateRoi.unit || 'x'}
              label={missionGauges.aggregateRoi.label || 'Aggregate ROI'}
              sublabel={missionGauges.aggregateRoi.sublabel || 'Mission value / spend'}
              tone={missionGauges.aggregateRoi.tone || 'sage'}
              deltaLabel={missionGauges.aggregateRoi.deltaLabel}
              deltaTone={missionGauges.aggregateRoi.deltaTone || 'sage'}
              richTooltip={missionGauges.aggregateRoi.richTooltip}
            />
          ) : (
            <div className="card" data-testid="roi-gauge-absent">
              <EmptyState
                title="Aggregate ROI not reported"
                description="The mission-impact source did not provide an ROI rollup — nothing is derived client-side."
              />
            </div>
          )}
          {missionGauges.missionOutcome?.value != null ? (
            <GaugeCard
              id={missionGauges.missionOutcome.id || 'mission-outcome'}
              value={missionGauges.missionOutcome.value}
              label={missionGauges.missionOutcome.label || 'Mission outcome score'}
              sublabel={missionGauges.missionOutcome.sublabel}
              tone={missionGauges.missionOutcome.tone || 'blue'}
              deltaLabel={missionGauges.missionOutcome.deltaLabel}
              deltaTone={missionGauges.missionOutcome.deltaTone || 'blue'}
              richTooltip={missionGauges.missionOutcome.richTooltip}
            />
          ) : avgMission != null ? (
            <GaugeCard
              id="mission-outcome"
              value={avgMission}
              label="Mission outcome score"
              sublabel={derivedMissionLabel}
              tone="blue"
            />
          ) : (
            <div className="card" data-testid="mission-gauge-absent">
              <EmptyState
                title="Mission outcome not reported"
                description="No portfolio reports a mission-impact score, so no outcome mean is shown."
              />
            </div>
          )}
        </div>

        <div className="span-7 card mi-heatmap">
          <header className="row spread">
            <div>
              <div className="eyebrow">Redundancy heatmap</div>
              <h2 className="card-title">Portfolio scoring by mission dimension</h2>
            </div>
          </header>
          {heatmap.cells.length ? (
            <MissionImpactHeatmap
              rows={heatmap.rows}
              columns={heatmap.columns}
              cells={heatmap.cells}
              cellSize={48}
            />
          ) : (
            <div data-testid="heatmap-empty-state">
              <EmptyState title="No scoring available" />
            </div>
          )}
        </div>
        <div className="span-5">
          {advisorRuns.loading ? (
            <ChartSkeleton height={320} />
          ) : advisorRun ? (
            <div className="stack">
              <AiRecommendationCard
                title="AI Reallocation Advisor"
                subtitle={`${advisorReviewLabel} · ${advisorRun.modelId || advisorRun.provider || 'model unrecorded'}`}
                recommendations={advisorRecommendations}
                onAccept={acceptRecommendation}
                onDismiss={dismissRecommendation}
              />
              <p className="muted small mono" data-testid="advisor-run-meta">
                Run {advisorRun.id} · generated{' '}
                {formatRunTimestamp(advisorRun.completedAt || advisorRun.createdAt)} ·{' '}
                {(advisorRun.citations || []).length} citations
              </p>
            </div>
          ) : (
            <div className="card" data-testid="advisor-empty-state">
              <EmptyState
                title="No advisor run available"
                description="Reallocation guidance renders only from a governed server-side AI run — a persisted record with citations, model metadata, and a human-review state. Runs are generated by the resource-optimizer agent and reviewed in the AI orchestration hub; nothing is invented client-side."
              />
            </div>
          )}
        </div>

        <div className="span-12 card mi-kpis">
          <header className="row spread">
            <div>
              <div className="eyebrow">Mission KPIs</div>
              <h2 className="card-title">Top alignment indicators</h2>
            </div>
            <span className="muted small">
              {totalPortfolios} portfolios · {kpiList.length} KPIs tracked
            </span>
          </header>
          {kpiList.length === 0 ? (
            <EmptyState title="No KPIs loaded" />
          ) : (
            <div className="mi-kpi-grid">
              {kpiPager.items.map((k) => {
                const cur = k.currentValue ?? k.value ?? '—';
                const target = k.target ?? k.targetValue;
                return (
                  <article key={k.id} className="mi-kpi-card">
                    <div className="eyebrow">{humanizeEnum(k.scopeType || k.category || 'KPI')}</div>
                    <strong>{k.name || k.title}</strong>
                    <div className="mi-kpi-value">
                      <span className="mono">{cur}</span>
                      {target ? <span className="muted small">/ {target}</span> : null}
                    </div>
                    <p className="muted small">
                      {k.description?.slice(0, 100) || k.summary?.slice(0, 100) || ''}
                    </p>
                  </article>
                );
              })}
            </div>
          )}
          <PageViewerControls
            page={kpiPager.page}
            label="mission KPIs"
            pageSize={kpiPager.pageSize}
            onOffsetChange={kpiPager.setOffset}
            onPageSizeChange={kpiPager.setPageSize}
          />
        </div>

        <div className="span-12 card mi-briefings">
          <header className="row spread">
            <div>
              <div className="eyebrow">Executive briefing generation</div>
              <h2 className="card-title">Briefings &amp; exports</h2>
            </div>
            <button
              className="btn outline sm"
              onClick={generateBriefing}
              disabled={briefingMut.running}
            >
              New briefing
            </button>
          </header>
          {briefings.length === 0 ? (
            <EmptyState
              title="No mission-impact briefings yet"
              description="Generate one above to attach citations and approve for export."
            />
          ) : (
            <div className="mi-briefing-grid">
              {briefingPager.items.map((b) => (
                <BriefingExportCard
                  key={b.id}
                  briefing={b}
                  onOpen={(brief) => navigate(`/briefings/${brief.id}`)}
                  onExportHtml={(brief) => exportBriefing(brief, 'html')}
                  onExportPdf={(brief) => exportBriefing(brief, 'pdf')}
                  onApprove={approveBriefing}
                  busy={exportMut.running || approveMut.running}
                />
              ))}
            </div>
          )}
          <PageViewerControls
            page={briefingPager.page}
            label="briefings"
            pageSize={briefingPager.pageSize}
            onOffsetChange={briefingPager.setOffset}
            onPageSizeChange={briefingPager.setPageSize}
          />
        </div>
      </section>
    </>
  );
}
